
import { Link } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { FAQ, faqCategories } from '@/data/support/faqs';

interface FAQSectionProps {
  faqs: FAQ[];
}

const FAQSection = ({ faqs }: FAQSectionProps) => {
  const getCategoryName = (categoryId: string) => {
    return faqCategories.find(cat => cat.id === categoryId)?.name || categoryId;
  };

  if (faqs.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-xl font-bold mb-2">No FAQs Found</h3>
        <p className="text-muted-foreground mb-6">
          Try adjusting your search terms or category filters.
        </p>
        <p className="text-sm text-muted-foreground">
          Can't find what you're looking for?{' '}
          <Link to="/contact" className="text-primary hover:underline">
            Contact our support team
          </Link>
        </p>
      </div>
    );
  }

  const faqsByCategory = faqs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {} as Record<string, FAQ[]>);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Outer accordion: one item per category */}
      <Accordion type="single" collapsible className="space-y-3">
        {Object.entries(faqsByCategory).map(([categoryId, categoryFAQs]) => (
          <AccordionItem
            key={categoryId}
            value={categoryId}
            className="border rounded-lg px-4"
          >
            <AccordionTrigger className="min-h-[44px] text-left hover:no-underline py-5">
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold text-foreground">
                  {getCategoryName(categoryId)}
                </span>
                <Badge variant="outline" className="text-xs">
                  {categoryFAQs.length}
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              {/* Inner accordion: individual FAQs */}
              <Accordion type="single" collapsible className="w-full space-y-2 pb-2">
                {categoryFAQs.map((faq) => (
                  <AccordionItem
                    key={faq.id}
                    value={faq.id}
                    className="bg-card border rounded-lg px-5"
                  >
                    <AccordionTrigger className="text-left hover:no-underline py-4">
                      <span className="font-medium text-foreground text-base pr-2">
                        {faq.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-4">
                      <p className="text-muted-foreground leading-relaxed mb-4">
                        {faq.answer}
                      </p>
                      {faq.relatedLinks && faq.relatedLinks.length > 0 && (
                        <div className="border-t pt-4">
                          <h4 className="font-medium text-foreground mb-2 text-sm">Related Links:</h4>
                          <div className="flex flex-wrap gap-3">
                            {faq.relatedLinks.map((link, linkIndex) => (
                              <Link
                                key={linkIndex}
                                to={link.url}
                                className="inline-flex items-center text-primary hover:underline text-sm"
                              >
                                {link.text}
                                <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-1" />
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

export default FAQSection;
